import { useState } from "react";
import { UserPlus, UserMinus, ChevronDown, ChevronUp, Check } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dropdown } from "../../components/ui/dropdown";
import { MultiSelectDropdown } from "../../components/ui/multi-select-dropdown";
import { useToast } from "../../components/ui/toast";
import { ROLE_OPTIONS, type TeamMember, type TeamRole } from "../settings/types";
import { cn } from "../../lib/utils";
import { crmApi } from "../crm/api";
import type { CrmMember } from "../crm/types";
import { PERMISSION_CATEGORIES } from "./permissions";
import type { CategoryEntitlement, MemberScopeMode } from "../../context/auth-me";
import type { MemberAccessWrite } from "../settings/settings-api";
import type { OwnerInvitation } from "../settings/types";
import { ASSIGNABLE_ROLE_OPTIONS, type AssignableRole, type StoredScope } from "./access-model";
import { InviteForm } from "./invite-form";
import { InvitationsList } from "./invitations-list";
import { MemberAccessEditor } from "./member-access-editor";
import type { InvitationsState } from "./use-invitations";
import {
  canAccessChatbot,
  canAccessSupervision,
  canAccessVoiceCloning,
  canAccessWorkflows,
  isAdminUser,
  isTimelinerUser,
} from "../../lib/userAccess";
import type { AuthUser } from "../../context/AuthContext";

const CRM_FALLBACK_ROLE_NAME: Record<string, string> = { manager: "Sales Manager", agent: "Sales Agent" };

// A category whose whole tab is a per-account, off-by-default grant: granting
// a member permission inside it is pointless (and confusing) while the org
// itself can't see that tab at all. Categories with no gate here (agents,
// calls, billing, ...) are core areas every org has, so they always show.
const CATEGORY_GATES: Record<string, (user: AuthUser | null) => boolean> = {
  workflows: canAccessWorkflows,
  voice_cloning: canAccessVoiceCloning,
  supervision: canAccessSupervision,
  chatbot: canAccessChatbot,
  whatsapp: canAccessChatbot,
  timeliner: (user) => isAdminUser(user) || isTimelinerUser(user),
};

/** Owner controls that exist only where core turned org_invitations / member_grants on. */
export interface OwnerAccessControls {
  /** Non-null when org_invitations is on. */
  invitations: InvitationsState | null;
  memberGrants: boolean;
  entitlements: Record<string, CategoryEntitlement> | null | undefined;
  memberScope: MemberScopeMode;
  onInvite: (
    email: string, role: AssignableRole, access: { permissions: string[]; resource_scope: StoredScope | null },
  ) => Promise<OwnerInvitation>;
  onSaveAccess: (id: string, access: MemberAccessWrite) => Promise<void>;
  onStatus: (id: string, status: "active" | "suspended") => Promise<void>;
}

export function UsersTab({ members, user, canManageVoices, orgVoices, orgInstances, orgInstancesError, onRole, onRemove, onInvite, onPermissions, onVoices, onWhatsAppInstances, owner }: {
  members: TeamMember[];
  /** The logged-in org owner viewing this page -- gates which permission
   * categories are even worth showing (see CATEGORY_GATES above). */
  user: AuthUser | null;
  /** Only an owner may hand out voices; the server enforces the same. */
  canManageVoices: boolean;
  /** The organisation's voice inventory: what an owner may hand out. */
  orgVoices: { local_voice_name: string; gender: string }[];
  /** The account's WhatsApp numbers: what an owner may hand out. */
  orgInstances: { instance_name: string; phone_number: string | null }[];
  /** Set when the number inventory failed to load (vs genuinely empty). */
  orgInstancesError?: string | null;
  onRole: (id: string, role: TeamRole) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
  onInvite: (email: string, role: TeamRole) => Promise<unknown>;
  onPermissions: (id: string, permissions: string[]) => Promise<void>;
  onVoices: (id: string, allowedVoices: string[]) => Promise<void>;
  onWhatsAppInstances: (id: string, allowedInstances: string[]) => Promise<void>;
  /** Absent: today's UI exactly. */
  owner?: OwnerAccessControls;
}) {
  const toast = useToast();
  const cache = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("member");
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  const valid = /.+@.+\..+/.test(email);
  // CRM roster + role definitions. The CRM team endpoint only returns the
  // whole roster to managers/admins (others see just themselves), so the CRM
  // block below appears exactly where the viewer is allowed to see it.
  const crmTeam = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team, retry: false });
  const crmRoles = useQuery({ queryKey: ["crm-roles"], queryFn: crmApi.roles, retry: false });
  const crmRoleMap = new Map((crmRoles.data ?? []).map(r => [r.id, r]));
  const assignableCrmRoles = (crmRoles.data ?? []).filter(r => r.active);

  const crmRowFor = (member: TeamMember): CrmMember | undefined =>
    crmTeam.data?.members.find(r => r.id === member.id)
    ?? crmTeam.data?.members.find(r => (r.email || "").toLowerCase() === (member.email || "").toLowerCase());

  const crmRoleNameOf = (roleId: string): string => {
    if (roleId === "admin") return "Business Admin";
    return crmRoleMap.get(roleId)?.name ?? CRM_FALLBACK_ROLE_NAME[roleId] ?? roleId;
  };

  const changeCrmRole = async (member: TeamMember, crmRow: CrmMember, nextRole: string) => {
    try {
      await crmApi.editAgent(member.id, { role: nextRole, active: crmRow.active });
      await cache.invalidateQueries({ queryKey: ["crm-team"] });
      toast.success(`${member.name}'s CRM role is now ${crmRoleNameOf(nextRole)}`);
    } catch (error) {
      toast.danger("CRM role update failed", errorMessage(error));
    }
  };

  const errorMessage = (error: unknown) => {
    const detail = (error as any)?.response?.data?.detail;
    return typeof detail === "string" ? detail : "Please try again.";
  };

  const togglePermission = async (memberId: string, currentPermissions: string[], permissionId: string) => {
    const newPerms = currentPermissions.includes(permissionId)
      ? currentPermissions.filter(p => p !== permissionId)
      : [...currentPermissions, permissionId];

    try {
      await onPermissions(memberId, newPerms);
    } catch (error) {
      toast.danger("Permission update failed", errorMessage(error));
    }
  };

  const toggleVoice = async (memberId: string, current: string[], voiceName: string) => {
    const next = current.includes(voiceName)
      ? current.filter((v) => v !== voiceName)
      : [...current, voiceName];
    try {
      await onVoices(memberId, next);
    } catch (error) {
      toast.danger("Voice access update failed", errorMessage(error));
    }
  };

  const setInstances = async (memberId: string, next: string[]) => {
    try {
      await onWhatsAppInstances(memberId, next);
    } catch (error) {
      toast.danger("WhatsApp access update failed", errorMessage(error));
    }
  };

  const changeRole = async (member: TeamMember, nextRole: TeamRole) => {
    try {
      await onRole(member.id, nextRole);
      toast.success(`${member.name} is now ${nextRole}`);
    } catch (error) {
      toast.danger("Role update failed", errorMessage(error));
    }
  };

  const invite = async () => {
    try {
      await onInvite(email, role);
      toast.success(`Invite sent to ${email}`);
      setEmail("");
    } catch (error) {
      toast.danger("Invite failed", errorMessage(error));
    }
  };

  const remove = async (member: TeamMember) => {
    if (!window.confirm(`Remove ${member.name} from the workspace?`)) return;

    try {
      await onRemove(member.id);
      toast.success(`${member.name} removed`);
    } catch (error) {
      toast.danger("Remove failed", errorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      {/* Invite */}
      {owner?.invitations ? (
        <>
          <InviteForm
            entitlements={owner.entitlements}
            memberScope={owner.memberScope}
            onInvite={owner.onInvite}
            onIssued={owner.invitations.remember}
            onResendPending={owner.invitations.resendPendingFor}
          />
          <InvitationsList state={owner.invitations} />
        </>
      ) : (
      <div className="flex flex-wrap items-center gap-3 rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-4">
        <input
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="teammate@company.com"
          className="w-72 rounded-lg border border-line px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
        />
        <Dropdown ariaLabel="Invite role" className="w-36" value={role} options={ROLE_OPTIONS} onChange={setRole} />
        <button
          disabled={!valid}
          onClick={() => { void invite(); }}
          className={valid ? "flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover" : "flex cursor-not-allowed items-center gap-2 rounded-lg bg-line px-4 py-2 text-sm font-semibold text-ink-3"}
        >
          <UserPlus className="h-4 w-4" /> Invite
        </button>
      </div>
      )}

      {/* Members */}
      <ul className="space-y-3">
        {members.map((m) => {
          const isExpanded = expandedMember === m.id;
          const isOwner = m.role === "owner";
          const currentPerms = m.permissions || [];
          const currentVoices = m.allowedVoices || [];
          const currentInstances = m.allowedWhatsAppInstances ?? null;
          const accessEditor = !!owner?.memberGrants && !isOwner && m.status !== "invited";
          const roleOptions = owner?.invitations && m.status === "invited" ? ASSIGNABLE_ROLE_OPTIONS : ROLE_OPTIONS;
          // This user's CRM role (defined in crm_roles). Matched by user id,
          // falling back to email — the CRM roster uses the same user ids.
          // Assigning the role is what grants its permissions. Fresh invitees
          // have no CRM row yet — admins see them as Unassigned and can give
          // them a role right here (admins see the full roster, so a missing
          // row genuinely means "no role", not "hidden from me").
          const liveRow = crmRowFor(m);
          const crmRow: CrmMember | undefined = liveRow
            ?? (crmTeam.data?.role === "admin"
              ? { id: m.id, name: m.name, email: m.email, phone: "", role: "", active: true }
              : undefined);
          const crmOptions = assignableCrmRoles.map(r => ({ value: r.id, label: r.name }));
          if (crmRow && crmRow.role !== "" && !crmOptions.some(o => o.value === crmRow.role)) {
            crmOptions.push({ value: crmRow.role, label: crmRoleNameOf(crmRow.role) });
          }
          crmOptions.push({ value: "", label: "Unassigned" });
          const canEditCrm = !!crmRow && crmTeam.data?.role === "admin" && crmRow.role !== "admin" && crmOptions.length > 0;

          return (
            <li key={m.id} className="rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] overflow-hidden transition-all hover:shadow-card-hover">
              <div className="flex items-center gap-4 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-soft font-bold text-ink flex-shrink-0">
                  {m.name.slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold text-ink">{m.name}</p>
                    {m.status === "invited" && (
                      <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600 border border-amber-500/20">
                        Pending
                      </span>
                    )}
                    {owner && m.status === "suspended" && (
                      <span className="rounded-full border border-line bg-soft px-2 py-0.5 text-xs font-medium text-ink-3">
                        Suspended
                      </span>
                    )}
                  </div>
                  <p className="truncate font-mono text-xs text-ink-3">{m.email}</p>
                </div>
                
                <div className="flex items-center gap-2">
                  {accessEditor ? (
                    // The role is saved with the rest of the access, in the editor below.
                    <span className="w-32 text-sm capitalize text-ink-2">{m.role}</span>
                  ) : (
                  <Dropdown<TeamRole>
                    ariaLabel={`Role for ${m.name}`} className="w-32"
                    value={m.role} options={roleOptions}
                    onChange={(nextRole) => { void changeRole(m, nextRole); }}
                  />
                  )}
                  <button
                    aria-label={`Remove ${m.name}`}
                    onClick={() => { void remove(m); }}
                    className="rounded-lg p-2 text-ink-3 transition-colors hover:bg-danger-soft hover:text-danger"
                  >
                    <UserMinus className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setExpandedMember(isExpanded ? null : m.id)}
                    className="rounded-lg p-2 text-ink-3 transition-colors hover:bg-soft"
                  >
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Permissions Panel */}
              {isExpanded && (
                <div className="border-t border-line bg-soft/30 p-4 pl-18">
                  {accessEditor && owner ? (
                    <MemberAccessEditor
                      // A new version (a save, or the reload after a 409) restarts from the server's row.
                      key={`${m.id}:${m.version ?? 0}`}
                      member={m}
                      entitlements={owner.entitlements}
                      memberScope={owner.memberScope}
                      onSave={owner.onSaveAccess}
                      onStatus={owner.onStatus}
                    />
                  ) : (<>
                  <h4 className="text-sm font-semibold text-ink mb-3">Permissions</h4>

                  {isOwner ? (
                    <div className="p-3 bg-brand-soft rounded-lg text-sm text-brand font-medium">
                      Owners have full access to all workspace features.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {PERMISSION_CATEGORIES.filter(category => {
                        const gate = CATEGORY_GATES[category.id];
                        return !gate || gate(user);
                      }).map(category => (
                        <div key={category.id} className="space-y-2">
                          <h5 className="text-xs font-semibold text-ink-3 uppercase tracking-wider">{category.label}</h5>
                          <div className="space-y-1.5">
                            {category.permissions.map(perm => {
                              const isChecked = currentPerms.includes(perm.id);
                              return (
                                <label key={perm.id} className="flex items-start gap-2 cursor-pointer group">
                                  <div className={cn(
                                    "mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors",
                                    isChecked ? "bg-brand border-brand" : "border-line bg-white group-hover:border-brand/50"
                                  )}>
                                    {isChecked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                  </div>
                                  <input 
                                    type="checkbox" 
                                    className="hidden" 
                                    checked={isChecked}
                                    onChange={() => { void togglePermission(m.id, currentPerms, perm.id); }}
                                  />
                                  <span className="text-sm text-ink-2 group-hover:text-ink">{perm.label}</span>
                                </label>
                              );
                            })}
                          </div>
                          {category.id === "whatsapp" && !isOwner && canManageVoices && (
                            <div className="pt-1">
                              <p className="mb-1.5 text-xs font-semibold text-ink-3 uppercase tracking-wider">Numbers</p>
                              {orgInstancesError ? (
                                <p role="alert" className="rounded-lg bg-danger-soft p-2.5 text-xs text-danger">
                                  {orgInstancesError}
                                </p>
                              ) : orgInstances.length === 0 ? (
                                <p className="rounded-lg bg-soft p-2.5 text-xs text-ink-3">
                                  No WhatsApp numbers yet.
                                </p>
                              ) : (
                                <MultiSelectDropdown
                                  ariaLabel={`WhatsApp numbers for ${m.name}`}
                                  value={currentInstances ?? []}
                                  options={orgInstances.map((instance) => ({
                                    value: instance.instance_name,
                                    label: instance.phone_number ? `+${instance.phone_number}` : instance.instance_name,
                                    hint: instance.phone_number ? instance.instance_name : undefined,
                                  }))}
                                  onChange={(next) => { void setInstances(m.id, next); }}
                                  placeholder="Select numbers…"
                                  dropDown
                                />
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      {/* CRM last — one row per CRM role; the checked one is this
                          user's role and carries its permissions. An admin ticks
                          another role to assign it; everyone else sees it
                          read-only. */}
                      {crmRow && (
                        <div className="space-y-2">
                          <h5 className="text-xs font-semibold text-ink-3 uppercase tracking-wider">CRM</h5>
                          <div className="space-y-1.5">
                            {crmOptions.map(opt => {
                              const isChecked = opt.value === crmRow.role;
                              return canEditCrm ? (
                                <label key={opt.value} className="flex items-start gap-2 cursor-pointer group">
                                  <div className={cn(
                                    "mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors",
                                    isChecked ? "bg-brand border-brand" : "border-line bg-white group-hover:border-brand/50"
                                  )}>
                                    {isChecked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                  </div>
                                  <input
                                    type="checkbox"
                                    className="hidden"
                                    checked={isChecked}
                                    onChange={() => { if (!isChecked) void changeCrmRole(m, crmRow, opt.value); }}
                                  />
                                  <span className="text-sm text-ink-2 group-hover:text-ink">{opt.label}</span>
                                </label>
                              ) : (
                                <div key={opt.value} className="flex items-start gap-2">
                                  <div className={cn(
                                    "mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border",
                                    isChecked ? "bg-brand border-brand" : "border-line bg-white"
                                  )}>
                                    {isChecked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                  </div>
                                  <span className="text-sm text-ink-2">{opt.label}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  </>)}

                  {!isOwner && canManageVoices && (
                    <>
                      <h4 className="mb-1 mt-6 text-sm font-semibold text-ink">Voice access</h4>
                      <p className="mb-3 text-xs text-ink-3">
                        Which of your organisation&apos;s voices this member may use. A voice you
                        clone is not shared with anyone until you add it here.
                      </p>
                      {orgVoices.length === 0 ? (
                        <p className="rounded-lg bg-soft p-3 text-sm text-ink-2">
                          Your organisation has no voices yet.
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                          {orgVoices.map((voice) => {
                            const isChecked = currentVoices.includes(voice.local_voice_name);
                            return (
                              <label
                                key={voice.local_voice_name}
                                className="group flex cursor-pointer items-start gap-2"
                              >
                                <div
                                  className={cn(
                                    "mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors",
                                    isChecked
                                      ? "border-brand bg-brand"
                                      : "border-line bg-white group-hover:border-brand/50",
                                  )}
                                >
                                  {isChecked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                </div>
                                <input
                                  type="checkbox"
                                  className="hidden"
                                  checked={isChecked}
                                  onChange={() => {
                                    void toggleVoice(m.id, currentVoices, voice.local_voice_name);
                                  }}
                                />
                                <span className="text-sm text-ink-2 group-hover:text-ink">
                                  {voice.local_voice_name}
                                  {voice.gender ? (
                                    <span className="text-ink-3"> · {voice.gender}</span>
                                  ) : null}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
